import { useEffect, useRef, useState, memo, useCallback, useMemo } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  ArrowDown,
  Font,
  Images,
  Type3,
  File02,
  Facebook,
  MessageChat01,
  ArrowSkip,
  TikTok,
  ArrowLeft,
  ArrowRight,
} from 'blaze-ui/icons'
import { Instagram, Twitter, LinkedIn } from 'blaze-ui/icons/16'
import { Palette } from 'blaze-ui/icons/20/Palette'
import Styles from './ContentGallery.module.scss'
import { observer } from 'mobx-react-lite'

import { ContentPreviewProvider, useContentPreviewState } from './ContentPreviewProvider'
import { LoadingProvider, useLoadingContext } from './LoadingContext'
import { SharedBrandKitProvider } from './SharedBrandKitProvider'
import { usePollAICaptionGeneration } from 'hooks/queries/aiTemplates/usePollAICaptionGeneration'
import { processToolCalls } from 'components/Design/CaptionTab/processToolCalls'
import { AiClient } from 'api/clients/AiClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { Archetype } from 'types'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import { InstagramStoryPreview } from 'components/Previews/InstagramStoryPreview/InstagramStoryPreview'
import { ExternalIntegrationEnum, InstagramIntegration } from 'api/clients/ExternalIntegrationsClient'

gsap.registerPlugin(ScrollTrigger)

const CONFIG = {
  INTERCOM_HELP_MESSAGE:
    "Hello! Can you help me with my content, its looking off? I'm on the content gallery step right now in Autopilot Onboarding.",
  ANIMATION: {
    wordDelay: 80,
    baseDuration: 400,
    cycleInterval: 5000,
    initialScale: 0.94,
    initialBlur: 4,
    animationDuration: 0.9,
    staggerDelay: 0.06,
    entranceDelay: 0.2,
  },
}

const CYCLING_TEXTS = [
  [
    ['Cooking', 'up', 'some'],
    ['beautiful', 'content', 'for', 'you'],
  ],
  [
    ['Crafting', 'content', 'that'],
    ['converts', 'for', 'you'],
  ],
  [
    ['Growing', 'your', 'audience'],
    ['while', 'you', 'sleep'],
  ],
  [
    ['Generating', 'leads'],
    ['with', 'every', 'post'],
  ],
  [
    ['Learning', 'what', 'works', 'best'],
    ['for', 'your', 'brand'],
  ],
]

const CONTENT_ARCHETYPES = [
  Archetype.INSTAGRAM_POST_PHOTO_TEXT,
  Archetype.VIDEO_INSTAGRAM_TEXT,
  Archetype.HEADER_PARAGRAPH_PHOTO,
  Archetype.PHOTO_HEADER_PARAGRAPH,
  Archetype.PHOTO_PARAGRAPH,
  Archetype.PHOTO_HEADER_PARAGRAPH_BOXED,
  Archetype.VIDEO_HEADER_PARAGRAPH_BOXED,
]

const INITIAL_POSITIONS = [
  { x: 0.01, y: 0.25 }, // Card 0: INSTAGRAM_POST_PHOTO_TEXT
  { x: 0.8, y: 0.01 }, // Card 1: VIDEO_INSTAGRAM_TEXT
  { x: 0.6, y: -0.1 }, // Card 2: FACEBOOK HEADER_PARAGRAPH_PHOTO
  { x: 0.25, y: -0.1 }, // Card 3: TWITTER PHOTO_HEADER_PARAGRAPH
  { x: 0.63, y: 0.8 }, // Card 4: LINKEDIN PHOTO_PARAGRAPH
  { x: 0.85, y: 0.75 }, // Card 5: FACEBOOK PHOTO_HEADER_PARAGRAPH_BOXED
  { x: 0.2, y: 0.7 }, // Card 6: TIKTOK VIDEO_HEADER_PARAGRAPH_BOXED
]

const FINAL_POSITIONS = [
  { x: -600, y: 850 }, // Card 0 - INSTAGRAM_POST_PHOTO_TEXT
  { x: 300, y: 800 }, // Card 1 - VIDEO_INSTAGRAM_TEXT
  { x: 0, y: 600 }, // Card 2 - FACEBOOK HEADER_PARAGRAPH_PHOTO
  { x: -410, y: 600 }, // Card 3 - TWITTER PHOTO_HEADER_PARAGRAPH
  { x: 590, y: 600 }, // Card 4 - LINKEDIN PHOTO_PARAGRAPH
  { x: 700, y: 850 }, // Card 5 - FACEBOOK PHOTO_HEADER_PARAGRAPH_BOXED
  { x: -200, y: 860 }, // Card 6 - TIKTOK VIDEO_HEADER_PARAGRAPH_BOXED
]

const CARD_Z_INDEXES = [
  2, // Card 0 - INSTAGRAM_POST_PHOTO_TEXT
  3, // Card 1 - VIDEO_INSTAGRAM_TEXT
  1, // Card 2 - FACEBOOK HEADER_PARAGRAPH_PHOTO
  1, // Card 3 - TWITTER PHOTO_HEADER_PARAGRAPH
  1, // Card 4 - LINKEDIN PHOTO_PARAGRAPH
  3, // Card 5 - FACEBOOK PHOTO_HEADER_PARAGRAPH_BOXED
  2, // Card 6 - TIKTOK VIDEO_HEADER_PARAGRAPH_BOXED
]

const LOADING_COLORS = [
  { r: 102, g: 126, b: 234 }, // #667eea
  { r: 118, g: 75, b: 162 }, // #764ba2
  { r: 240, g: 147, b: 251 }, // #f093fb
  { r: 245, g: 87, b: 108 }, // #f5576c
  { r: 79, g: 172, b: 254 }, // #4facfe
  { r: 0, g: 242, b: 254 }, // #00f2fe
  { r: 138, g: 43, b: 226 }, // #8a2be2
]
const PHASE_CLASS_MAP = {
  exiting: Styles.wordExiting,
  entering: Styles.wordEntering,
  visible: Styles.wordVisible,
} as const

interface ArchetypeDimensions {
  generationWidth: number
  generationHeight: number
  displayWidth: number
  displayHeight: number
  aspectRatio: number
  previewType: 'instagram-story' | 'instagram-post' | 'facebook' | 'x' | 'boxed' | 'generic' | 'custom' | 'caption-only'
  scaleMode: 'contain' | 'cover' | 'fill'
}

const getArchetypeDimensions = (archetype: Archetype): ArchetypeDimensions => {
  switch (archetype) {
    case Archetype.INSTAGRAM_POST_PHOTO_TEXT:
      return {
        generationWidth: 1080,
        generationHeight: 1350,
        displayWidth: 265,
        displayHeight: 405,
        aspectRatio: 4 / 5,
        previewType: 'custom',
        scaleMode: 'contain',
      }
    case Archetype.INSTAGRAM_POST_PHOTO_TEXT_LOGO:
      return {
        generationWidth: 1080,
        generationHeight: 1350,
        displayWidth: 280,
        displayHeight: 420,
        aspectRatio: 4 / 5,
        previewType: 'custom',
        scaleMode: 'contain',
      }
    case Archetype.HEADER_PARAGRAPH_PHOTO:
      return {
        generationWidth: 1080,
        generationHeight: 1080,
        displayWidth: 280,
        displayHeight: 350,
        aspectRatio: 1,
        previewType: 'custom',
        scaleMode: 'contain',
      }
    case Archetype.HEADER_PHOTO:
      return {
        generationWidth: 1080,
        generationHeight: 1080,
        displayWidth: 250,
        displayHeight: 325,
        aspectRatio: 1,
        previewType: 'custom',
        scaleMode: 'contain',
      }
    case Archetype.PHOTO_HEADER_PARAGRAPH:
    case Archetype.PHOTO_PARAGRAPH:
      return {
        generationWidth: 1080,
        generationHeight: 1350,
        displayWidth: 260,
        displayHeight: 360,
        aspectRatio: 4 / 5,
        previewType: 'custom',
        scaleMode: 'contain',
      }
    case Archetype.PHOTO_HEADER_PARAGRAPH_BOXED:
      return {
        generationWidth: 1080,
        generationHeight: 1350,
        displayWidth: 250,
        displayHeight: 380,
        aspectRatio: 4 / 5,
        previewType: 'custom',
        scaleMode: 'contain',
      }
    case Archetype.VIDEO_INSTAGRAM_TEXT:
      return {
        generationWidth: 1080,
        generationHeight: 1920,
        displayWidth: 280,
        displayHeight: 560,
        aspectRatio: 9 / 16,
        previewType: 'instagram-story',
        scaleMode: 'contain',
      }
    case Archetype.VIDEO_HEADER_PARAGRAPH_BOXED:
      return {
        generationWidth: 1080,
        generationHeight: 1350,
        displayWidth: 280,
        displayHeight: 380,
        aspectRatio: 4 / 5,
        previewType: 'custom',
        scaleMode: 'contain',
      }
    default:
      return {
        generationWidth: 1080,
        generationHeight: 1080,
        displayWidth: 312,
        displayHeight: 468,
        aspectRatio: 1,
        previewType: 'custom',
        scaleMode: 'contain',
      }
  }
}

const getArchetypeIconAndLabel = (archetype: Archetype, index?: number) => {
  switch (archetype) {
    case Archetype.INSTAGRAM_POST_PHOTO_TEXT:
    case Archetype.INSTAGRAM_POST_PHOTO_TEXT_LOGO:
      return { icon: <Instagram size={16} />, label: 'Post' }
    case Archetype.VIDEO_INSTAGRAM_TEXT:
      return { icon: <Instagram size={16} />, label: 'Video' }
    case Archetype.VIDEO_HEADER_PARAGRAPH_BOXED:
      return { icon: <TikTok size={16} />, label: 'Video' }
    case Archetype.PHOTO_HEADER_PARAGRAPH:
      return { icon: <Twitter size={16} />, label: 'Post' }
    case Archetype.PHOTO_PARAGRAPH:
      return { icon: <LinkedIn size={16} />, label: 'Post' }
    case Archetype.HEADER_PARAGRAPH_PHOTO:
    case Archetype.PHOTO_HEADER_PARAGRAPH_BOXED:
    default:
      return { icon: <Facebook size={16} />, label: 'Post' }
  }
}

const CARD_DIMENSIONS = CONTENT_ARCHETYPES.map(getArchetypeDimensions)

const shouldGenerateCaption = (archetype: Archetype): boolean => {
  const excludedArchetypes = [
    Archetype.PHOTO_HEADER_PARAGRAPH,
    Archetype.VIDEO_INSTAGRAM_TEXT,
    Archetype.VIDEO_HEADER_PARAGRAPH_BOXED,
  ]
  return !excludedArchetypes.includes(archetype)
}
const LoadingIndicator = memo(
  ({
    archetype,
    loadingPercentage,
    gradientStart,
    gradientMid,
    gradientEnd,
  }: {
    archetype: Archetype
    loadingPercentage: number
    gradientStart: string
    gradientMid: string
    gradientEnd: string
  }) => {
    const { icon, label } = getArchetypeIconAndLabel(archetype)

    return (
      <>
        <div className={Styles.loadingIconContainer}>
          {icon}
          <span className={Styles.loadingIconText}>{label}</span>
        </div>
        <div
          className={`${Styles.loadingBackground} ${Styles.loadingBackgroundGradient}`}
          style={
            {
              '--gradient-start': gradientStart,
              '--gradient-mid': gradientMid,
              '--gradient-end': gradientEnd,
            } as React.CSSProperties
          }
        />
        <div className={Styles.loadingProgressContainer}>
          <div
            className={`${Styles.loadingProgressBar} ${Styles.loadingProgressWidth}`}
            style={{ '--progress-width': `${loadingPercentage}%` } as React.CSSProperties}
          />
        </div>
      </>
    )
  },
)

LoadingIndicator.displayName = 'LoadingIndicator'

const SimpleContentPreview = memo(
  ({ index, dimensions, archetype }: { index: number; dimensions: ArchetypeDimensions; archetype: Archetype }) => {
    const { generatedContent, isGenerating, brandKit } = useContentPreviewState()
    const { markCardAsLoaded, loadingPercentage } = useLoadingContext()
    const [hasNotifiedLoaded, setHasNotifiedLoaded] = useState(false)
    const [showContentImmediate, setShowContentImmediate] = useState(false)
    const [hasAnimated, setHasAnimated] = useState(false)
    const [generatedCaption, setGeneratedCaption] = useState<string>('')
    const [captionRequestId, setCaptionRequestId] = useState<string>('')
    const [isGeneratingCaption, setIsGeneratingCaption] = useState(false)
    const [truncatedCaption, setTruncatedCaption] = useState<string>('')
    const [needsTruncation, setNeedsTruncation] = useState(false)
    const cardRef = useRef<HTMLDivElement>(null)

    const archetypeIconAndLabel = useMemo(() => getArchetypeIconAndLabel(archetype, index), [archetype, index])

    const truncateText = (text: string): { truncated: string; needsTruncation: boolean } => {
      const availableWidth = 220 - 24
      const charWidth = 6
      const charsPerLine = Math.floor(availableWidth / charWidth)
      const maxLines = 2
      const totalChars = charsPerLine * maxLines
      const maxSafeLength = totalChars - 7

      if (text.length <= maxSafeLength) {
        return { truncated: text, needsTruncation: false }
      }

      let truncateAt = maxSafeLength
      const lastSpace = text.lastIndexOf(' ', truncateAt)
      if (lastSpace > maxSafeLength * 0.7) {
        truncateAt = lastSpace
      }

      return {
        truncated: text.substring(0, truncateAt).trim(),
        needsTruncation: true,
      }
    }

    useEffect(() => {
      if (generatedCaption) {
        const { truncated, needsTruncation } = truncateText(generatedCaption)
        setTruncatedCaption(truncated)
        setNeedsTruncation(needsTruncation)
      }
    }, [generatedCaption])

    usePollAICaptionGeneration(
      {
        id: captionRequestId,
      },
      {
        enabled: shouldGenerateCaption(archetype) && !!captionRequestId,
        onSuccess: async (res) => {
          if (['done', 'failed'].includes(res.status)) {
            try {
              if (res.status === 'done') {
                const newCaptions = processToolCalls(res.data)
                if (newCaptions && newCaptions.length > 0) {
                  setGeneratedCaption(newCaptions[0])
                }
              }
            } catch (e) {
            } finally {
              setIsGeneratingCaption(false)
              setCaptionRequestId('')
            }
          }
        },
        onError: (e) => {
          setIsGeneratingCaption(false)
          setCaptionRequestId('')
        },
      },
    )

    useEffect(() => {
      if (
        brandKit &&
        !generatedCaption &&
        !isGeneratingCaption &&
        !captionRequestId &&
        shouldGenerateCaption(archetype)
      ) {
        const generateCaption = async () => {
          setIsGeneratingCaption(true)
          try {
            const selectedTopic =
              brandKit.brand_voice?.voice_params?.purpose?.[0] ||
              brandKit.brand_voice?.voice_params?.tone?.[0] ||
              `Create engaging content for ${brandKit.name}`

            const response = await AiClient.captionGeneration({
              prompt: selectedTopic,
              brandVoiceId: brandKit.brand_voice?.id,
              language: 'English',
            })

            if (response.type === SUCCESS_RESPONSE_TYPE) {
              setCaptionRequestId(response.data)
            } else {
              setIsGeneratingCaption(false)
            }
          } catch (error) {
            setIsGeneratingCaption(false)
          }
        }

        generateCaption()
      }
    }, [brandKit, generatedCaption, isGeneratingCaption, captionRequestId, archetype])

    useEffect(() => {
      if (!isGenerating && generatedContent && !hasNotifiedLoaded) {
        markCardAsLoaded(index)
        setHasNotifiedLoaded(true)
      }
    }, [isGenerating, generatedContent, hasNotifiedLoaded, index, markCardAsLoaded])

    useEffect(() => {
      if (!isGenerating && generatedContent && !showContentImmediate && !hasAnimated && cardRef.current) {
        const cardElement = cardRef.current.closest('[data-content-item]') as HTMLElement

        if (cardElement) {
          const staggerDelay = index * 150 + 200

          setTimeout(() => {
            gsap.set(cardElement, {
              opacity: 0,
              scale: CONFIG.ANIMATION.initialScale,
              filter: `blur(${CONFIG.ANIMATION.initialBlur}px)`,
            })

            setShowContentImmediate(true)
            setHasAnimated(true)

            gsap.to(cardElement, {
              opacity: 1,
              scale: 1,
              filter: 'blur(0px)',
              duration: CONFIG.ANIMATION.animationDuration,
              ease: 'power3.out',
            })
          }, staggerDelay)
        }
      }
    }, [isGenerating, generatedContent, showContentImmediate, hasAnimated, index])

    const designContent = generatedContent ? <DesignContentPreview content={generatedContent} /> : null

    const rgb = LOADING_COLORS[index % LOADING_COLORS.length] || LOADING_COLORS[0]
    const {
      start: gradientStart,
      mid: gradientMid,
      end: gradientEnd,
    } = {
      start: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08)`,
      mid: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
      end: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`,
    }

    if (dimensions.previewType === 'instagram-story') {
      const baseName = brandKit?.name ?? 'Brand'
      const avatar = brandKit?.brand_visual_kit?.primary_logo?.url ?? 'https://via.placeholder.com/150'

      const instagramIntegration: InstagramIntegration = {
        provider: ExternalIntegrationEnum.Instagram,
        id: 1,
        provider_id: '1',
        name: baseName,
        picture: avatar,
        account_type: 'profile',
        type: 'ExternalIntegration',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        scopes: [],
        last_heartbeat_checked_at: new Date().toISOString(),
        last_disconnected_email_sent_at: null,
        disconnected_email_count: 0,
        is_active: true,
        additional_data: {
          username: baseName,
          profile_picture_url: avatar,
          id: '1',
          name: baseName,
        },
      }

      return (
        <div ref={cardRef} className={Styles.instagramStoryContainer}>
          {!showContentImmediate && (
            <div className={Styles.instagramStoryLoadingOverlay}>
              <LoadingIndicator
                archetype={archetype}
                loadingPercentage={loadingPercentage}
                gradientStart={gradientStart}
                gradientMid={gradientMid}
                gradientEnd={gradientEnd}
              />
            </div>
          )}

          <div className={`${Styles.instagramStoryContent} ${showContentImmediate ? Styles.visible : ''}`}>
            <InstagramStoryPreview
              containerClassName="instagram-story-preview-container"
              integration={instagramIntegration}
              customPreviewContent={designContent}
            />
          </div>
        </div>
      )
    }

    return (
      <div ref={cardRef} className={Styles.storyContainer}>
        <div
          className={`${Styles.storyBackground} ${Styles.storyBackgroundLoading} ${
            showContentImmediate ? Styles.hidden : ''
          }`}
        >
          <LoadingIndicator
            archetype={archetype}
            loadingPercentage={loadingPercentage}
            gradientStart={gradientStart}
            gradientMid={gradientMid}
            gradientEnd={gradientEnd}
          />
        </div>

        <div
          className={`${Styles.storyWrapper} ${Styles.storyWrapperContent} ${
            showContentImmediate ? Styles.visible : ''
          }`}
        >
          {showContentImmediate && (
            <div
              className={`${Styles.postHeader} ${
                archetype === Archetype.PHOTO_HEADER_PARAGRAPH ? Styles.postHeaderTwitter : ''
              }`}
            >
              {archetypeIconAndLabel.icon}
              <span className={Styles.postTitle}>{archetypeIconAndLabel.label}</span>
            </div>
          )}
          {showContentImmediate && archetype !== Archetype.PHOTO_HEADER_PARAGRAPH && truncatedCaption && (
            <div className={Styles.postCaption}>
              <div className={Styles.captionText}>
                {truncatedCaption}
                {needsTruncation && <span className={Styles.moreText}>...more</span>}
              </div>
            </div>
          )}
          {designContent}
        </div>
      </div>
    )
  },
)

SimpleContentPreview.displayName = 'SimpleContentPreview'

const ContentCard = memo(
  ({ archetype, dimensions, index }: { archetype: Archetype; dimensions: ArchetypeDimensions; index: number }) => {
    return (
      <div
        data-content-item
        data-archetype={archetype}
        data-card-index={index}
        data-preview-type={dimensions.previewType}
        className={`${Styles.contentPreview} ${Styles.contentCardDynamic}`}
        style={
          {
            '--card-width': `${dimensions.displayWidth}px`,
            '--card-height': `${dimensions.displayHeight}px`,
            '--card-min-height': `${dimensions.displayHeight}px`,
            '--card-max-height': `${dimensions.displayHeight}px`,
            '--aspect-ratio': dimensions.aspectRatio,
            '--scale-mode': dimensions.scaleMode,
          } as React.CSSProperties
        }
      >
        <ContentPreviewProvider
          archetype={archetype}
          width={dimensions.generationWidth}
          height={dimensions.generationHeight}
          index={index}
        >
          <SimpleContentPreview index={index} dimensions={dimensions} archetype={archetype} />
        </ContentPreviewProvider>
      </div>
    )
  },
)

ContentCard.displayName = 'ContentCard'

const CyclingText = memo(() => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0)
  const [animationPhase, setAnimationPhase] = useState<'visible' | 'exiting' | 'entering'>('visible')

  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = []

    const interval = setInterval(() => {
      const currentText = CYCLING_TEXTS[currentTextIndex]
      const totalWords = currentText.flat().length

      setAnimationPhase('exiting')

      const exitDuration = totalWords * CONFIG.ANIMATION.wordDelay + CONFIG.ANIMATION.baseDuration
      const exitTimeout = setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % CYCLING_TEXTS.length)
        setAnimationPhase('entering')
      }, exitDuration)
      timeouts.push(exitTimeout)

      const newText = CYCLING_TEXTS[(currentTextIndex + 1) % CYCLING_TEXTS.length]
      const newTotalWords = newText.flat().length
      const enterDuration = exitDuration + newTotalWords * CONFIG.ANIMATION.wordDelay + CONFIG.ANIMATION.baseDuration
      const enterTimeout = setTimeout(() => {
        setAnimationPhase('visible')
      }, enterDuration)
      timeouts.push(enterTimeout)
    }, CONFIG.ANIMATION.cycleInterval)

    return () => {
      clearInterval(interval)
      timeouts.forEach(clearTimeout)
    }
  }, [currentTextIndex])

  const renderText = useCallback(
    (textLines: string[][]) => {
      let globalWordIndex = 0

      return textLines.map((line, lineIndex) => (
        <div key={lineIndex} className={Styles.textLine}>
          {line.map((word, wordInLineIndex) => {
            const currentGlobalIndex = globalWordIndex++
            const phaseClass = PHASE_CLASS_MAP[animationPhase]
            const animationDelay =
              animationPhase !== 'visible' ? `${currentGlobalIndex * CONFIG.ANIMATION.wordDelay}ms` : undefined

            return (
              <span
                key={`${word}-${lineIndex}-${wordInLineIndex}-${animationPhase}`}
                className={`${Styles.word} ${phaseClass} ${Styles.wordAnimated}`}
                style={{ '--animation-delay': animationDelay } as React.CSSProperties}
              >
                {word}
              </span>
            )
          })}
        </div>
      ))
    },
    [animationPhase],
  )

  return (
    <div className={Styles.cyclingTextContainer}>
      <h1 className={Styles.mainTitle}>{renderText(CYCLING_TEXTS[currentTextIndex])}</h1>
    </div>
  )
})

CyclingText.displayName = 'CyclingText'

const BottomSkipButton = memo(
  ({ hasAnyContentLoaded, onNext }: { hasAnyContentLoaded: boolean; onNext: () => void }) => {
    const buttonClassName = `${Styles.bottomSkipButton} ${hasAnyContentLoaded ? Styles.hasContent : ''}`

    return (
      <button onClick={onNext} className={buttonClassName} data-heap-id="content-gallery-skip">
        <div
          className={`${Styles.buttonText} ${hasAnyContentLoaded ? Styles.hidden : Styles.visible}`}
          style={{ position: hasAnyContentLoaded ? 'absolute' : 'static' }}
        >
          <ArrowSkip size={20} />
          Skip
        </div>

        <div
          className={`${Styles.buttonText} ${hasAnyContentLoaded ? Styles.visible : Styles.hidden}`}
          style={{ position: hasAnyContentLoaded ? 'static' : 'absolute' }}
        >
          Let's Get Started
          <ArrowRight size={20} />
        </div>
      </button>
    )
  },
)

BottomSkipButton.displayName = 'BottomSkipButton'

class ScrollLockManager {
  private static instance: ScrollLockManager | null = null
  private isLocked = false
  private originalStyles: { [key: string]: string } = {}
  private preventScroll: ((e: Event) => boolean) | null = null
  private containerLocks: Array<{ element: HTMLElement; originalOverflow: string }> = []
  private originalScrollIntoView: typeof Element.prototype.scrollIntoView | null = null
  private originalScrollIntoViewIfNeeded: typeof Element.prototype.scrollIntoViewIfNeeded | null = null
  private originalFocus: typeof HTMLElement.prototype.focus | null = null

  static getInstance(): ScrollLockManager {
    if (!ScrollLockManager.instance) {
      ScrollLockManager.instance = new ScrollLockManager()
    }
    return ScrollLockManager.instance
  }

  lock(): () => void {
    if (this.isLocked) return () => this.unlock()

    this.isLocked = true
    const body = document.body
    const html = document.documentElement
    const scrollY = window.pageYOffset
    const scrollX = window.pageXOffset

    this.originalStyles = {
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
    }

    body.style.setProperty('overflow', 'hidden', 'important')
    html.style.setProperty('overflow', 'hidden', 'important')
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.left = `-${scrollX}px`

    const containers = [
      document.querySelector('[class*="MultiStepPageLayout_main"]'),
      document.querySelector('[class*="main"]'),
      document.querySelector('[class*="transitionContainer"]'),
      document.querySelector('[class*="stepContent"]'),
      document.querySelector('[class*="page"]'),
    ].filter(Boolean) as HTMLElement[]

    containers.forEach((container) => {
      const originalOverflow = container.style.overflowY || window.getComputedStyle(container).overflowY
      this.containerLocks.push({ element: container, originalOverflow })
      container.style.overflowY = 'hidden'
    })

    this.preventScroll = (e: Event) => {
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()
      return false
    }

    const events = ['wheel', 'touchmove', 'scroll', 'touchstart', 'touchend', 'keydown']
    events.forEach((event) => {
      if (this.preventScroll) {
        document.addEventListener(event, this.preventScroll, { capture: true, passive: false })
        window.addEventListener(event, this.preventScroll, { capture: true, passive: false })
      }
    })

    this.originalScrollIntoView = Element.prototype.scrollIntoView
    Element.prototype.scrollIntoView = function () {
      return
    }

    if (Element.prototype.scrollIntoViewIfNeeded) {
      this.originalScrollIntoViewIfNeeded = Element.prototype.scrollIntoViewIfNeeded
      Element.prototype.scrollIntoViewIfNeeded = function () {
        return
      }
    }

    this.originalFocus = HTMLElement.prototype.focus
    HTMLElement.prototype.focus = function (options?: FocusOptions) {
      return ScrollLockManager.instance?.originalFocus?.call(this, { ...options, preventScroll: true })
    }

    return () => this.unlock()
  }

  unlock(): void {
    if (!this.isLocked) return

    const body = document.body
    const html = document.documentElement
    const scrollY = Math.abs(parseInt(body.style.top) || 0)
    const scrollX = Math.abs(parseInt(body.style.left) || 0)

    if (this.preventScroll) {
      const events = ['wheel', 'touchmove', 'scroll', 'touchstart', 'touchend', 'keydown']
      const preventScrollRef = this.preventScroll
      events.forEach((event) => {
        document.removeEventListener(event, preventScrollRef, { capture: true })
        window.removeEventListener(event, preventScrollRef, { capture: true })
      })
      this.preventScroll = null
    }

    if (this.originalScrollIntoView) {
      Element.prototype.scrollIntoView = this.originalScrollIntoView
      this.originalScrollIntoView = null
    }
    if (this.originalScrollIntoViewIfNeeded) {
      Element.prototype.scrollIntoViewIfNeeded = this.originalScrollIntoViewIfNeeded
      this.originalScrollIntoViewIfNeeded = null
    }
    if (this.originalFocus) {
      HTMLElement.prototype.focus = this.originalFocus
      this.originalFocus = null
    }

    this.containerLocks.forEach(({ element, originalOverflow }) => {
      element.style.overflowY = originalOverflow
    })
    this.containerLocks = []

    Object.entries(this.originalStyles).forEach(([key, value]) => {
      const property = key
        .replace(/([A-Z])/g, '-$1')
        .toLowerCase()
        .replace('body-', '')
        .replace('html-', '')
      const element = key.startsWith('html') ? html : body
      const cssProperty = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
      ;(element.style as any)[cssProperty] = value
    })

    this.originalStyles = {}
    this.isLocked = false
    window.scrollTo(scrollX, scrollY)
  }

  forceUnlock(): void {
    const body = document.body
    const html = document.documentElement

    if (this.preventScroll) {
      const events = ['wheel', 'touchmove', 'scroll', 'touchstart', 'touchend', 'keydown']
      const preventScrollRef = this.preventScroll
      events.forEach((event) => {
        document.removeEventListener(event, preventScrollRef, { capture: true })
        window.removeEventListener(event, preventScrollRef, { capture: true })
      })
      this.preventScroll = null
    }

    if (this.originalScrollIntoView) {
      Element.prototype.scrollIntoView = this.originalScrollIntoView
      this.originalScrollIntoView = null
    }
    if (this.originalScrollIntoViewIfNeeded) {
      Element.prototype.scrollIntoViewIfNeeded = this.originalScrollIntoViewIfNeeded
      this.originalScrollIntoViewIfNeeded = null
    }
    if (this.originalFocus) {
      HTMLElement.prototype.focus = this.originalFocus
      this.originalFocus = null
    }

    body.style.removeProperty('overflow')
    body.style.removeProperty('position')
    body.style.removeProperty('top')
    body.style.removeProperty('left')
    html.style.removeProperty('overflow')

    this.containerLocks.forEach(({ element, originalOverflow }) => {
      element.style.overflowY = originalOverflow || ''
    })
    this.containerLocks = []

    const multiStepElements = [
      document.querySelector('[class*="MultiStepPageLayout_page"]'),
      document.querySelector('[class*="MultiStepPageLayout_main"]'),
    ].filter(Boolean) as HTMLElement[]

    multiStepElements.forEach((element) => {
      element.style.setProperty('overflow-y', 'auto', 'important')
    })

    this.originalStyles = {}
    this.isLocked = false
  }
}

interface ContentGalleryProps {
  brandKitId?: number
  onNext: () => void
  onBack: () => void
  onBackToProfile?: () => void
}

const ContentGalleryInner = observer(({ brandKitId, onNext, onBack, onBackToProfile }: ContentGalleryProps) => {
  const { loadingComplete, setTotalCards, loadingPercentage, setIsTransitioning, isTransitioning, loadedCards } =
    useLoadingContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const centralTextRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const [contentLoaded, setContentLoaded] = useState(false)
  const [showEditOptions, setShowEditOptions] = useState(false)
  const scrollLockManager = useRef<ScrollLockManager>(ScrollLockManager.getInstance())
  const scrollLockCleanupRef = useRef<(() => void) | null>(null)
  const hasAnyContentLoaded = loadedCards.size > 0

  useEffect(() => {
    if (typeof window.Intercom === 'function') {
      window.Intercom('hide')
    }

    return () => {
      if (typeof window.Intercom === 'function') {
        window.Intercom('update')
      }
    }
  }, [])

  useEffect(() => {
    setTotalCards(CONTENT_ARCHETYPES.length)
  }, [setTotalCards])

  useEffect(() => {
    if (loadingComplete && !contentLoaded) {
      setIsTransitioning(true)
      setTimeout(() => {
        setContentLoaded(true)
        setIsTransitioning(false)
      }, 600)
    }
  }, [loadingComplete, contentLoaded, setIsTransitioning])

  useEffect(() => {
    const manager = scrollLockManager.current
    const cleanup = manager.lock()
    scrollLockCleanupRef.current = cleanup

    if (contentLoaded) {
      cleanup()
      scrollLockCleanupRef.current = null
    }

    return () => {
      cleanup()
      manager.forceUnlock()
    }
  }, [contentLoaded])

  const handleNext = useCallback(() => {
    scrollLockManager.current.forceUnlock()
    onNext()
  }, [onNext])

  const handleBack = useCallback(() => {
    scrollLockManager.current.forceUnlock()
    onBack()
  }, [onBack])

  const handleBackToProfile = useCallback(() => {
    scrollLockManager.current.forceUnlock()
    onBackToProfile ? onBackToProfile() : onBack()
  }, [onBackToProfile, onBack])

  const getConvergencePositions = useCallback(() => {
    const { innerWidth, innerHeight } = window
    const centerX = innerWidth / 2
    const centerY = innerHeight / 2

    return FINAL_POSITIONS.map((pos) => ({
      x: centerX + pos.x,
      y: centerY + pos.y,
    }))
  }, [])

  useEffect(() => {
    if (!containerRef.current || !centralTextRef.current) return

    const container = containerRef.current
    const centralText = centralTextRef.current

    const generateInitialPositions = () => {
      const { innerWidth: viewportWidth, innerHeight: viewportHeight } = window

      return CARD_DIMENSIONS.map((cardDim, index) => {
        const layout = INITIAL_POSITIONS[index]
        const x = layout.x * viewportWidth
        const y = layout.y * viewportHeight
        const z = CARD_Z_INDEXES[index] || 1

        return {
          x,
          y,
          z,
        }
      })
    }

    requestAnimationFrame(() => {
      const contentItems = container.querySelectorAll('[data-content-item]')

      if (contentItems.length === 0) return
      ScrollTrigger.getAll().forEach((trigger: ScrollTrigger) => trigger.kill())

      const storedInitialPositions: { x: number; y: number }[] = []

      const initialPositions = generateInitialPositions()

      contentItems.forEach((item, index) => {
        const element = item as HTMLElement
        const position = initialPositions[index]

        if (!position) return

        storedInitialPositions[index] = { x: position.x, y: position.y }

        gsap.set(element, {
          x: position.x,
          y: position.y,
          zIndex: position.z,
          transformOrigin: '0 0',
        })
      })

      gsap.from(centralText, {
        duration: 1,
        scale: 0.8,
        opacity: 0,
        ease: 'power2.out',
      })

      const setupScrollAnimations = () => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: container,
            scroller: container,
            start: 'top top',
            end: '+=600vh',
            scrub: true,
            invalidateOnRefresh: true,
          },
        })

        const convergencePositions = getConvergencePositions()

        contentItems.forEach((item, index) => {
          const element = item as HTMLElement
          const initialPos = storedInitialPositions[index]
          const convergencePos = convergencePositions[index]
          const cardDim = CARD_DIMENSIONS[index]

          if (!initialPos || !convergencePos || !cardDim) return

          const finalX = convergencePos.x - cardDim.displayWidth / 2
          const finalY = convergencePos.y - cardDim.displayHeight / 2

          tl.to(
            element,
            {
              x: finalX,
              y: finalY,
              scale: 1,
              ease: 'power1.out',
              duration: 0.5,
            },
            0,
          )
        })

        tl.to(
          centralText,
          {
            opacity: 0,
            ease: 'power2.out',
            duration: 0.2,
          },
          0,
        )
      }

      setupScrollAnimations()

      return () => {
        ScrollTrigger.getAll().forEach((trigger: ScrollTrigger) => trigger.kill())
      }
    })
  }, [contentLoaded, getConvergencePositions])

  return (
    <>
      <div ref={containerRef} className={Styles.galleryContainer}>
        <div className={Styles.singleViewport}>
          <div ref={centralTextRef} className={`${Styles.centralText} ${Styles.centralTextContainer}`}>
            {!contentLoaded ? (
              <div
                className={`${Styles.loadingContent} ${Styles.loadingContentContainer} ${
                  isTransitioning ? Styles.transitioning : ''
                }`}
              >
                <CyclingText />
                <div className={`${Styles.loadingTextWrapper} ${Styles.loadingTextWrapperSpacing}`}>
                  <p className={Styles.loadingSubtext}>Generating content...</p>
                  <div className={Styles.styledTextContainer}>
                    <span className={Styles.styledTextContent}>{loadingPercentage}%</span>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`${Styles.readyContent} ${Styles.readyContentContainer} ${
                  contentLoaded ? Styles.visible : ''
                }`}
              >
                <h1 className={Styles.mainTitle}>
                  Welcome to the future
                  <br />
                  of your marketing
                </h1>
                <div className={Styles.styledTextContainerScroll}>
                  <div className={Styles.scrollHint}>
                    <span>Scroll to explore</span>
                    <ArrowDown className={Styles.downIcon} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {CONTENT_ARCHETYPES.map((currentArchetype, index) => {
            const dimensions = CARD_DIMENSIONS[index]

            return (
              <ContentCard
                key={`content-${index}`}
                archetype={currentArchetype}
                dimensions={dimensions}
                index={index}
              />
            )
          })}
        </div>

        <div ref={footerRef} className={Styles.navigationSection}>
          <div className={Styles.navigationContent}>
            {!showEditOptions && (
              <>
                <div className={Styles.navigationText}>
                  <h2
                    className={Styles.navigationTitle}
                    onClick={handleNext}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleNext()
                      }
                    }}
                    data-heap-id="content-gallery-accept"
                  >
                    Yes I want this content →
                  </h2>
                </div>

                <button
                  className={Styles.editOptionsButton}
                  onClick={() => setShowEditOptions(true)}
                  data-heap-id="content-gallery-edit-options"
                >
                  No, something looks off.
                </button>
              </>
            )}

            {showEditOptions && (
              <div className={Styles.editOptionsContainer}>
                <div className={Styles.editOptionsTextHigher}>No, something looks off.</div>

                <div className={Styles.navigationButtons}>
                  <button
                    onClick={handleBack}
                    className={Styles.categoryButtonColors}
                    aria-label="Colors"
                    data-heap-id="content-gallery-colors"
                  >
                    <Palette size={20} />
                    Colors
                  </button>

                  <button
                    onClick={handleBack}
                    className={Styles.categoryButtonFonts}
                    aria-label="Fonts"
                    data-heap-id="content-gallery-fonts"
                  >
                    <Font size={20} />
                    Fonts
                  </button>

                  <button
                    onClick={handleBack}
                    className={Styles.categoryButtonImages}
                    aria-label="Images"
                    data-heap-id="content-gallery-images"
                  >
                    <Images size={20} />
                    Images
                  </button>

                  <button
                    onClick={handleBackToProfile}
                    className={Styles.categoryButtonBusiness}
                    aria-label="Copy"
                    data-heap-id="content-gallery-copy"
                  >
                    <Type3 size={20} />
                    Copy
                  </button>

                  <button
                    onClick={handleBackToProfile}
                    className={Styles.categoryButtonInfo}
                    aria-label="Business Info"
                    data-heap-id="content-gallery-business-info"
                  >
                    <File02 size={20} />
                    Business Info
                  </button>

                  <button
                    onClick={() => {
                      if (typeof window !== 'undefined' && typeof window.Intercom === 'function') {
                        window.Intercom('showNewMessage', CONFIG.INTERCOM_HELP_MESSAGE)
                      }
                    }}
                    className={Styles.categoryButtonHelp}
                    aria-label="Something else"
                    data-heap-id="content-gallery-help"
                  >
                    <MessageChat01 size={20} />
                    Something else
                  </button>
                </div>

                <div className={Styles.editOptionsNavigation}>
                  <button
                    onClick={handleBack}
                    className={Styles.editOptionsBackButton}
                    data-heap-id="content-gallery-edit-back"
                  >
                    <ArrowLeft size={20} />
                    Back
                  </button>
                  <button
                    onClick={handleNext}
                    className={Styles.editOptionsSkipButton}
                    data-heap-id="content-gallery-edit-proceed"
                  >
                    <ArrowRight size={20} />
                    Continue
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={`${Styles.bottomNavigation} ${contentLoaded ? Styles.fadeOut : ''}`}>
          <button onClick={handleBack} className={Styles.bottomBackButton} data-heap-id="content-gallery-back">
            <ArrowLeft size={20} />
            Back
          </button>
          <BottomSkipButton hasAnyContentLoaded={hasAnyContentLoaded} onNext={handleNext} />
        </div>
      </div>
    </>
  )
})

export const ContentGallery = observer(({ brandKitId, onNext, onBack, onBackToProfile }: ContentGalleryProps) => {
  return (
    <SharedBrandKitProvider brandKitId={brandKitId}>
      <LoadingProvider>
        <ContentGalleryInner
          brandKitId={brandKitId}
          onNext={onNext}
          onBack={onBack}
          onBackToProfile={onBackToProfile}
        />
      </LoadingProvider>
    </SharedBrandKitProvider>
  )
})
